import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetIpdMedicalCertificateById, UpdateIpdMedicalCertificate } from "../../../../api/ipd/ipdMedicalCertificateAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  docNo: yup.string().required("Doc No is required"),
  type: yup.string().required("Type is required"),
  ipdEntryId: yup.string().required("Room number is required"),
  certificateDetails: yup.array().of(
    yup.object().shape({
      date: yup.date().required("Date is required"),
      fromDate: yup.date().required("From Date is required"),
      toDate: yup.date().required("To Date is required"),
      diagnosis: yup.string().required("Diagnosis is required"),
      fitness: yup.string().required("fitness is required"),
    })
  ),
});

const EditIpdMedicalCertificateEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [ipdEntries, setIpdEntries] = useState([]);
  const [patientDetails, setPatientDetails] = useState({});
  const [type, setType] = useState("OPD");
  
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        control,
        watch,
        formState: { errors } } = useForm({
          resolver: yupResolver(schema),
          defaultValues: {
            certificateDetails: [
              {
                date: new Date().toISOString().split("T")[0],
                fromDate: new Date().toISOString().split("T")[0],
                toDate: new Date().toISOString().split("T")[0],
                diagnosis: "",
                fitness: ""
              }
            ]
          }
        });
  
    const {
        fields: certificateDetailsFields,
        append: appendCertificateDetails,
        remove: removeCertificateDetails
      } = useFieldArray({
        control,
        name: "certificateDetails"
      });

    const fetchIpdEntries = async () => {
      try {
        const response = await GetIpdEntries();
        if (response.success) {
          setIpdEntries(response.data);
        } else {
          showToast("error", response.message);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };

  const fetchData = async () => {
  try {
    const response = await GetIpdMedicalCertificateById(id);

    if (response.success) {
      const data = response.data;

      // 👉 Convert date values inside certificateDetails[]
      const updatedCertificates = data.certificateDetails?.map(item => ({
        ...item,
        date: item.date ? item.date.split("T")[0] : "",
        fromDate: item.fromDate ? item.fromDate.split("T")[0] : "",
        toDate: item.toDate ? item.toDate.split("T")[0] : ""
      }));

      // 👉 Set back into form
      setValue("certificateDetails", updatedCertificates);

      // 👉 Set other simple fields
      Object.entries(data).forEach(([key, value]) => {
        if (!["certificateDetails"].includes(key)) {
          if (["date", "fromDate", "toDate"].includes(key) && value) {
            setValue(key, value.split("T")[0]);
          } else {
            setValue(key, value);
          }
        }
      });

      // 👉 Set patient details
      setPatientDetails(data?.opdEntryId || data?.ipdEntryId || {});
      setValue("ipdEntryId", data?.ipdEntryId?._id || "");
    } else {
      showToast("error", response.message);
    }
  } catch (error) {
    showToast("error", error.message);
  }
};


  // Fetch existing data
  useEffect(
    () => {
      if (id) {
        fetchIpdEntries().then(() => {
            fetchData();
        });
      }
    },
    [id, setValue]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      
      const response = await UpdateIpdMedicalCertificate(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Ipd Medical Certificate" title="Edit Ipd Medical Certificate Entry" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Edit Ipd Medical Certificate Entry</h5>
                <Link
                  to={`/ipd/transaction/ipd-medical-certificate-entry/list`}
                >
                  <button className="btn btn-primary" id="list">
                    List Ipd Medical Certificate Entry
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Ipd Medical Certificate Entry
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    {/* Name */}
                    <div className="col-6">
                      <div className="form-group">
                        <label>Doc No</label>
                        <input
                          className="form-control"
                          {...register("docNo")}
                          placeholder="Doc No"
                          readOnly
                        />
                        {errors.docNo &&
                          <p className="text-danger">
                            {errors.docNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Type</label>
                        <select
                          className="form-control"
                          {...register("type")}
                          onChange={(e) => {setType(e.target.value);setPatientDetails({});setValue('opdEntryId', '');setValue('ipdEntryId', '');}}
                        >
                          <option value="IPD">IPD</option>
                        </select>
                        {errors.type &&
                          <p className="text-danger">
                            {errors.type.message}
                          </p>}
                      </div>
                    </div>
                    
                    <div className="col-6">
                    <div className="form-group">
                      <label>Ipd No</label>
                      <select
                        className="form-control"
                        {...register("ipdEntryId", {
                          onChange: (e) => {
                            setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                          }
                        })}
                      >
                        <option value="">Select Ipd No</option>
                        {ipdEntries.map(ipd =>
                          <option key={ipd._id} value={ipd._id}>
                            {ipd.ipdNo}
                          </option>
                        )}
                      </select>
                      {errors.ipdEntryId &&
                        <p className="text-danger">
                          {errors.ipdEntryId.message}
                        </p>}
                    </div>
                  </div>
                    
                  </div>
                  <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Patient Details
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>UHID No</label>
                        <input
                          className="form-control"
                          placeholder="UHID No"
                          value={patientDetails?.patientId?.uhidNo}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Ipd No</label>
                        <input
                          className="form-control"
                          placeholder="Opd No"
                          value={patientDetails?.ipdNo}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Patient</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.patientName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Guardian Name</label>
                        <input
                          className="form-control"
                          placeholder="Guardian Name"
                          value={patientDetails?.patientId?.guardianName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Age</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.age}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Case</label>
                        <input
                          className="form-control"
                          placeholder="Case"
                          value={patientDetails?.caseId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Consultant Doctor</label>
                        <input
                          className="form-control"
                          placeholder="Consultant Doctor"
                          value={patientDetails?.consultantDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>ReferenceDoctor</label>
                        <input
                          className="form-control"
                          placeholder="ReferenceDoctor"
                          value={patientDetails?.referenceDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          className="form-control"
                          placeholder="Address"
                          value={patientDetails?.patientId?.address}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Certificate Details
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-12 my-3">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Date</th>
                            <th>From Date</th>
                            <th>To Date</th>
                            <th>Diagnosis</th>
                            <th>fitness</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {certificateDetailsFields.map((field, index) => (
                            <tr key={field.id}>
                              <td>{index + 1}</td>
                              <td>
                                <input
                                  className="form-control"
                                  type="date"
                                  {...register(`certificateDetails.${index}.date`)}
                                  placeholder="Date"
                                />
                                {errors.certificateDetails?.[index]?.date && (
                                  <p className="text-danger">
                                    {errors.certificateDetails[index].date.message}
                                  </p>
                                )}
                              </td>
                              <td>
                                <input
                                  className="form-control"
                                  type="date"
                                  {...register(`certificateDetails.${index}.fromDate`)}
                                  placeholder="From Date"
                                />
                                {errors.certificateDetails?.[index]?.fromDate && (
                                  <p className="text-danger">
                                    {errors.certificateDetails[index].fromDate.message}
                                  </p>
                                )}
                              </td>
                              <td>
                                <input
                                  className="form-control"
                                  type="date"
                                  {...register(`certificateDetails.${index}.toDate`)}
                                  placeholder="To Date"
                                />
                                {errors.certificateDetails?.[index]?.toDate && (
                                  <p className="text-danger">
                                    {errors.certificateDetails[index].toDate.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <input
                                  className="form-control"
                                  type="text"
                                  {...register(`certificateDetails.${index}.diagnosis`)}
                                  placeholder="Diagnosis"
                                />
                                {errors.certificateDetails?.[index]?.diagnosis && (
                                  <p className="text-danger">
                                    {errors.certificateDetails[index].diagnosis.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <input
                                  className="form-control"
                                  type="text"
                                  {...register(`certificateDetails.${index}.fitness`)}
                                  placeholder="Fitness"
                                />
                                {errors.certificateDetails?.[index]?.fitness && (
                                  <p className="text-danger">
                                    {errors.certificateDetails[index].fitness.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <div className="d-flex">

                                  <button
                                    type="button"
                                    className="btn btn-danger me-2"
                                    onClick={() => certificateDetailsFields.length > 1 && removeCertificateDetails(index)}
                                  >
                                    -
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() =>
                                      appendCertificateDetails({
                                        date: "",
                                        fromDate: "",
                                        toDate: "",
                                        diagnosis: "",
                                        fitness: "",
                                      })
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>


                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label>Remark</label>
                        <textarea
                          className="form-control"
                          {...register("remark")}
                          placeholder="Remark"
                        />
                        {errors.remark &&
                          <p className="text-danger">
                            {errors.remark.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditIpdMedicalCertificateEntry;
